import React, { useEffect, useMemo } from "react";
import { Row as BootstrapRow, Col as BootstrapCol, Button } from "react-bootstrap";
import { Input, Select } from "antd";
import FormItem from "@/common/FormItem/FormItem";
import { useListBrandsQuery, useListBrandLocationsQuery } from "@/features/brands/brandsApi";
import {
  candidateMasterAccountIds,
  portfolioMasterAccountIds,
  resolveLocationLookup,
  type LocationLookupResult,
} from "@/features/accounts/clientSupportLookup";
import type { BrandLocationRow } from "@/features/brands/brandsApi";
import type { MasterAccountRow } from "@/features/accounts/accountsApi";
import styles from "@/pages/accounts.module.scss";

/**
 * Clients support-identification filter bar:
 *
 *   Find Client      | Brand  | Store #
 *   Location Address | Status
 *
 * Two intentionally DIFFERENT Brand relationships (never conflated):
 *
 * Brand alone  = Client Brand PORTFOLIO (canonical BrandMembership — the same
 *   relationship as "Brands for {Client}"). Answered from the already-loaded
 *   Clients list rows via row.brandIds; no Brand Locations request is made.
 *   "Which Clients have McDonald's in their portfolio?"
 *
 * Brand + Store # and/or Location Address = Location CURRENT OPERATOR.
 *   GET /sa/brands/{brandId}/locations → (storeNumber AND address_* match)
 *   → currentOperatorMasterAccountId → Client. Both criteria narrow the SAME
 *   canonical Location set. Only fetched when a non-empty Store # / address
 *   needs resolution. Several Locations may match a partial address — that is
 *   surfaced as an honest multiple-match list, never a silent first pick.
 *   The component only ever emits a LocationLookupResult / scope, so this
 *   lookup source can later be swapped for a dedicated indexed endpoint.
 *   "Who currently operates McDonald's Store #1321 at 123 Main Street?"
 *
 * Location Address WITHOUT Brand: the value is accepted and preserved, but no
 *   lookup runs (no bounded canonical cross-Brand address read exists — a
 *   global Location scan in the browser is forbidden). The operator is asked
 *   to select a Brand to narrow the lookup.
 *
 * Location Address is the PHYSICAL store address (canonical Location
 *   address_street/city/state/zipCode/country). It never searches
 *   MasterAccount/company address — Find Client owns person/company fields.
 *
 * MAAL (user authorization) is neither relationship and is never used here.
 *
 * Find Client free text — matching is performed by the table
 *   (clientSupportLookup.findClientHits) so it composes with the scope + Status.
 */

export type ClientSupportFilterValue = {
  brandId?: string;
  storeNumber: string;
  /** Physical Location address text (street / city / ZIP …). */
  locationAddress: string;
  findClient: string;
  status?: string;
};

export const EMPTY_CLIENT_SUPPORT_FILTERS: ClientSupportFilterValue = {
  brandId: undefined,
  storeNumber: "",
  locationAddress: "",
  findClient: "",
  status: undefined,
};

/** Scope derived from Brand (portfolio) or Brand + Store # (current operator). */
export type ClientLookupScope = {
  /** Master Account ids the table should be narrowed to; null = no narrowing. */
  masterAccountIds: string[] | null;
  result: LocationLookupResult;
  brandName: string | null;
  /** Which relationship produced masterAccountIds (null when no narrowing). */
  scopeKind: "portfolio" | "location" | null;
};

export const FIND_CLIENT_PLACEHOLDER = "Search name, email, company, customer #...";
export const LOCATION_ADDRESS_PLACEHOLDER = "Search street, city, ZIP...";
/** Max candidates listed in the multiple-match state (operator narrows further). */
const MAX_LISTED_CANDIDATES = 8;

type Props = {
  value: ClientSupportFilterValue;
  onChange: (patch: Partial<ClientSupportFilterValue>) => void;
  onScopeChange: (scope: ClientLookupScope) => void;
  onOpenClient: (masterAccountId: string) => void;
  /**
   * Already-loaded Clients list rows for the current view (same RTK Query
   * cache entry the table renders — no extra request). Brand-alone filtering
   * reads row.brandIds (canonical portfolio) from these.
   */
  clients?: MasterAccountRow[] | null;
  /** Bumped by Reset All so uncontrolled antd internals re-mount. */
  resetKey?: number;
};

const STATUS_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const ClientSupportFilters: React.FC<Props> = ({
  value,
  onChange,
  onScopeChange,
  onOpenClient,
  clients,
  resetKey = 0,
}) => {
  const brands = useListBrandsQuery({ view: "active" });
  const brandOptions = useMemo(
    () =>
      (brands.data?.brands || [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((b) => ({ label: b.name, value: b.brandId })),
    [brands.data],
  );
  const brandName = useMemo(
    () => brandOptions.find((o) => o.value === value.brandId)?.label ?? null,
    [brandOptions, value.brandId],
  );

  const storeNumber = value.storeNumber.trim();
  const address = (value.locationAddress || "").trim();
  const hasLocationCriteria = Boolean(storeNumber || address);

  // Brand Locations are only needed to resolve a Store # / Location Address
  // (current operator). Selecting a Brand alone must NOT fetch them —
  // portfolio comes from the loaded Client rows.
  const locations = useListBrandLocationsQuery(value.brandId as string, {
    skip: !value.brandId || !hasLocationCriteria,
  });

  const result: LocationLookupResult = useMemo(() => {
    if (!hasLocationCriteria) return { kind: "idle" };
    const c = { storeNumber, address };
    if (!value.brandId) return { kind: "needs-brand", ...c };
    if (locations.isLoading || locations.isFetching) return { kind: "loading", ...c };
    if (locations.isError)
      return { kind: "error", message: "Could not load this Brand's locations.", ...c };
    return resolveLocationLookup(locations.data?.locations, c);
  }, [hasLocationCriteria, storeNumber, address, value.brandId, locations]);

  // Brand alone → canonical portfolio membership from the loaded rows.
  const brandPortfolioIds = useMemo(
    () =>
      value.brandId && !hasLocationCriteria && clients
        ? portfolioMasterAccountIds(clients, value.brandId)
        : null,
    [value.brandId, hasLocationCriteria, clients],
  );

  // Derive table scope from exactly one relationship at a time:
  //   Store # / address present → Location current operator (canonical Location rows)
  //   Brand only                → Brand portfolio (row.brandIds, canonical BrandMembership)
  // Nothing is inferred; MAAL is never consulted.
  const { scopeIds, scopeKind } = useMemo<{
    scopeIds: string[] | null;
    scopeKind: ClientLookupScope["scopeKind"];
  }>(() => {
    if (result.kind === "resolved")
      return { scopeIds: [result.masterAccountId], scopeKind: "location" };
    if (result.kind === "no-location" || result.kind === "no-operator")
      return { scopeIds: [], scopeKind: "location" };
    if (result.kind === "multiple")
      // Table narrows to ALL candidate operators; the context lists them so the
      // operator disambiguates — nothing is picked silently.
      return { scopeIds: candidateMasterAccountIds(result.candidates), scopeKind: "location" };
    if (result.kind === "idle" && value.brandId && brandPortfolioIds)
      return { scopeIds: brandPortfolioIds, scopeKind: "portfolio" };
    return { scopeIds: null, scopeKind: null };
  }, [result, value.brandId, brandPortfolioIds]);

  const scopeKey = scopeIds ? scopeIds.join("|") : "null";
  useEffect(() => {
    onScopeChange({ masterAccountIds: scopeIds, result, brandName, scopeKind });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, scopeKind, result.kind, brandName, storeNumber, address]);

  const storeLabel = (sn: string) => `Store #${sn.replace(/^#\s*/, "")}`;
  /** "Store #1321 · Main St · 123 Main Street, Atlanta, GA 30303" from a canonical row. */
  const locationLabel = (loc: BrandLocationRow, typedStore?: string) => {
    const parts: string[] = [];
    const sn = loc.storeNumber || typedStore;
    if (sn) parts.push(storeLabel(sn));
    if (loc.locationName) parts.push(loc.locationName);
    if (loc.address) parts.push(loc.address);
    return parts.join(" · ");
  };
  /** What the operator typed, for the needs-brand / miss messages. */
  const criteriaLabel = (c: { storeNumber: string; address: string }) =>
    [c.storeNumber ? storeLabel(c.storeNumber) : null, c.address ? `“${c.address}”` : null]
      .filter(Boolean)
      .join(" at ");

  let context: React.ReactNode = null;
  if (result.kind === "needs-brand") {
    context = (
      <span className={styles.lookupHint} data-testid="store-lookup-needs-brand">
        {result.storeNumber
          ? `Select a Brand to look up ${criteriaLabel(result)}.`
          : "Select a Brand to narrow the Location address lookup."}
      </span>
    );
  } else if (result.kind === "loading") {
    context = (
      <span className={styles.lookupHint} data-testid="store-lookup-loading">
        Looking up {criteriaLabel(result)} under {brandName}…
      </span>
    );
  } else if (result.kind === "error") {
    context = (
      <span className={styles.lookupMiss} data-testid="store-lookup-error">
        {result.message}
      </span>
    );
  } else if (result.kind === "no-location") {
    context = (
      <span className={styles.lookupMiss} data-testid="store-lookup-no-location">
        No location found for {criteriaLabel(result)} under {brandName}.
      </span>
    );
  } else if (result.kind === "no-operator") {
    context = (
      <span className={styles.lookupMiss} data-testid="store-lookup-no-operator">
        {brandName} · {locationLabel(result.location, result.storeNumber)} exists but has
        no current operator.
      </span>
    );
  } else if (result.kind === "resolved") {
    context = (
      <span className={styles.lookupHit} data-testid="store-lookup-resolved">
        {brandName} · {locationLabel(result.location, result.storeNumber)}
        {" → "}
        <strong>{result.businessName || result.masterAccountId}</strong>
        <Button
          size="sm"
          variant="link"
          className={styles.lookupOpenLink}
          onClick={() => onOpenClient(result.masterAccountId)}
          data-testid="store-lookup-open-client"
        >
          Open Client Details
        </Button>
      </span>
    );
  } else if (result.kind === "multiple") {
    const listed = result.candidates.slice(0, MAX_LISTED_CANDIDATES);
    context = (
      <div data-testid="store-lookup-multiple">
        <span className={styles.lookupHint}>
          {brandName} · {result.candidates.length} locations match {criteriaLabel(result)} —
          narrow with Store #, more address text, or pick one:
        </span>
        <ul className={styles.lookupCandidates}>
          {listed.map((c) => (
            <li key={c.location.locationId} data-testid={`store-lookup-candidate-${c.location.locationId}`}>
              <span className={c.masterAccountId ? styles.lookupHit : styles.lookupMiss}>
                {locationLabel(c.location)}
                {" → "}
                {c.masterAccountId ? (
                  <strong>{c.businessName || c.masterAccountId}</strong>
                ) : (
                  "no current operator"
                )}
              </span>
              {c.location.storeNumber ? (
                <Button
                  size="sm"
                  variant="link"
                  className={styles.lookupOpenLink}
                  onClick={() => onChange({ storeNumber: c.location.storeNumber || "" })}
                  data-testid={`store-lookup-use-store-${c.location.locationId}`}
                >
                  Use {storeLabel(c.location.storeNumber)}
                </Button>
              ) : null}
              {c.masterAccountId ? (
                <Button
                  size="sm"
                  variant="link"
                  className={styles.lookupOpenLink}
                  onClick={() => onOpenClient(c.masterAccountId as string)}
                  data-testid={`store-lookup-open-client-${c.location.locationId}`}
                >
                  Open Client Details
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        {result.candidates.length > listed.length ? (
          <span className={styles.lookupHint}>
            …and {result.candidates.length - listed.length} more. Add Store # or more address text.
          </span>
        ) : null}
      </div>
    );
  } else if (value.brandId && brandPortfolioIds) {
    // Brand alone: PORTFOLIO wording only — never Location/operator wording.
    const n = brandPortfolioIds.length;
    context = (
      <span className={styles.lookupHint} data-testid="brand-scope-context">
        {n
          ? `${brandName} · ${n} client${n === 1 ? " has" : "s have"} this Brand in their portfolio`
          : `No clients have ${brandName} in their Brand portfolio.`}
      </span>
    );
  }

  return (
    <div className={styles.supportFilters} data-testid="client-support-filters">
      {/* Row 1: Find Client | Brand | Store #   Row 2: Location Address (under Find Client) | Status */}
      <BootstrapRow className="g-2">
        <BootstrapCol md={4}>
          <FormItem label="Find Client" className="customSelectWrapper" layout="vertical">
            <Input
              allowClear
              className={styles.supportInput}
              placeholder={FIND_CLIENT_PLACEHOLDER}
              value={value.findClient}
              onChange={(e) => onChange({ findClient: e.target.value })}
              aria-label="Find Client"
              data-testid="filter-find-client"
            />
          </FormItem>
        </BootstrapCol>
        <BootstrapCol md={4}>
          <FormItem label="Brand" className="customSelectWrapper" layout="vertical">
            <Select
              key={`brand-reset-${resetKey}`}
              showSearch
              allowClear
              loading={brands.isLoading}
              className={`customSelect ${styles.selectField}`}
              placeholder="Any brand"
              optionFilterProp="label"
              value={value.brandId}
              onChange={(v) => onChange({ brandId: v || undefined })}
              options={brandOptions}
              aria-label="Brand"
              data-testid="filter-brand"
            />
          </FormItem>
        </BootstrapCol>
        <BootstrapCol md={4}>
          <FormItem label="Store #" className="customSelectWrapper" layout="vertical">
            <Input
              allowClear
              className={styles.supportInput}
              placeholder="e.g. 1321"
              value={value.storeNumber}
              onChange={(e) => onChange({ storeNumber: e.target.value })}
              aria-label="Store #"
              data-testid="filter-store-number"
            />
          </FormItem>
        </BootstrapCol>
      </BootstrapRow>
      <BootstrapRow className="g-2">
        <BootstrapCol md={4}>
          <FormItem label="Location Address" className="customSelectWrapper" layout="vertical">
            <Input
              allowClear
              className={styles.supportInput}
              placeholder={LOCATION_ADDRESS_PLACEHOLDER}
              value={value.locationAddress}
              onChange={(e) => onChange({ locationAddress: e.target.value })}
              aria-label="Location Address"
              data-testid="filter-location-address"
            />
          </FormItem>
        </BootstrapCol>
        <BootstrapCol md={4}>
          <FormItem label="Status" className="customSelectWrapper" layout="vertical">
            <Select
              key={`status-reset-${resetKey}`}
              allowClear
              className={`customSelect ${styles.selectField}`}
              placeholder="Any status"
              value={value.status}
              onChange={(v) => onChange({ status: v || undefined })}
              options={STATUS_OPTIONS}
              aria-label="Status"
              data-testid="filter-status"
            />
          </FormItem>
        </BootstrapCol>
      </BootstrapRow>
      {context ? (
        <div className={styles.lookupContext} role="status">
          {context}
        </div>
      ) : null}
    </div>
  );
};

export default ClientSupportFilters;
